/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  isProgramError,
  type Address,
  type SOLANA_ERROR__INSTRUCTION_ERROR__CUSTOM,
  type SolanaError,
} from '@solana/kit';
import { DOPPLER_VESTING_PROGRAM_ADDRESS } from '../programs';

/** InvalidConfig: Invalid vesting configuration */
export const DOPPLER_VESTING_ERROR__INVALID_CONFIG = 0x1770; // 6000
/** TooManySchedules: Too many vesting schedules */
export const DOPPLER_VESTING_ERROR__TOO_MANY_SCHEDULES = 0x1771; // 6001
/** TooManyAllocations: Too many vesting allocations */
export const DOPPLER_VESTING_ERROR__TOO_MANY_ALLOCATIONS = 0x1772; // 6002
/** InvalidSchedule: Invalid vesting schedule */
export const DOPPLER_VESTING_ERROR__INVALID_SCHEDULE = 0x1773; // 6003
/** InvalidAllocation: Invalid vesting allocation */
export const DOPPLER_VESTING_ERROR__INVALID_ALLOCATION = 0x1774; // 6004
/** AllocationCapExceeded: Vesting allocation exceeds the permitted supply cap */
export const DOPPLER_VESTING_ERROR__ALLOCATION_CAP_EXCEEDED = 0x1775; // 6005
/** ArithmeticOverflow: Arithmetic overflow */
export const DOPPLER_VESTING_ERROR__ARITHMETIC_OVERFLOW = 0x1776; // 6006
/** InvalidLaunch: Invalid Initializer launch */
export const DOPPLER_VESTING_ERROR__INVALID_LAUNCH = 0x1777; // 6007
/** InvalidMint: Invalid base mint */
export const DOPPLER_VESTING_ERROR__INVALID_MINT = 0x1778; // 6008
/** InvalidTokenProgram: Invalid base token program */
export const DOPPLER_VESTING_ERROR__INVALID_TOKEN_PROGRAM = 0x1779; // 6009
/** VestingNotFunded: The launch has not funded vesting */
export const DOPPLER_VESTING_ERROR__VESTING_NOT_FUNDED = 0x177a; // 6010
/** InvalidBeneficiary: Invalid vesting beneficiary */
export const DOPPLER_VESTING_ERROR__INVALID_BENEFICIARY = 0x177b; // 6011
/** UnknownSchedule: Unknown vesting schedule */
export const DOPPLER_VESTING_ERROR__UNKNOWN_SCHEDULE = 0x177c; // 6012
/** NoTokensAvailable: No vested tokens are currently available */
export const DOPPLER_VESTING_ERROR__NO_TOKENS_AVAILABLE = 0x177d; // 6013
/** InsufficientAvailableAmount: Requested amount exceeds the vested amount available */
export const DOPPLER_VESTING_ERROR__INSUFFICIENT_AVAILABLE_AMOUNT = 0x177e; // 6014

export type DopplerVestingError =
  | typeof DOPPLER_VESTING_ERROR__ALLOCATION_CAP_EXCEEDED
  | typeof DOPPLER_VESTING_ERROR__ARITHMETIC_OVERFLOW
  | typeof DOPPLER_VESTING_ERROR__INSUFFICIENT_AVAILABLE_AMOUNT
  | typeof DOPPLER_VESTING_ERROR__INVALID_ALLOCATION
  | typeof DOPPLER_VESTING_ERROR__INVALID_BENEFICIARY
  | typeof DOPPLER_VESTING_ERROR__INVALID_CONFIG
  | typeof DOPPLER_VESTING_ERROR__INVALID_LAUNCH
  | typeof DOPPLER_VESTING_ERROR__INVALID_MINT
  | typeof DOPPLER_VESTING_ERROR__INVALID_SCHEDULE
  | typeof DOPPLER_VESTING_ERROR__INVALID_TOKEN_PROGRAM
  | typeof DOPPLER_VESTING_ERROR__NO_TOKENS_AVAILABLE
  | typeof DOPPLER_VESTING_ERROR__TOO_MANY_ALLOCATIONS
  | typeof DOPPLER_VESTING_ERROR__TOO_MANY_SCHEDULES
  | typeof DOPPLER_VESTING_ERROR__UNKNOWN_SCHEDULE
  | typeof DOPPLER_VESTING_ERROR__VESTING_NOT_FUNDED;

let dopplerVestingErrorMessages:
  | Record<DopplerVestingError, string>
  | undefined;
if (process.env.NODE_ENV !== 'production') {
  dopplerVestingErrorMessages = {
    [DOPPLER_VESTING_ERROR__ALLOCATION_CAP_EXCEEDED]: `Vesting allocation exceeds the permitted supply cap`,
    [DOPPLER_VESTING_ERROR__ARITHMETIC_OVERFLOW]: `Arithmetic overflow`,
    [DOPPLER_VESTING_ERROR__INSUFFICIENT_AVAILABLE_AMOUNT]: `Requested amount exceeds the vested amount available`,
    [DOPPLER_VESTING_ERROR__INVALID_ALLOCATION]: `Invalid vesting allocation`,
    [DOPPLER_VESTING_ERROR__INVALID_BENEFICIARY]: `Invalid vesting beneficiary`,
    [DOPPLER_VESTING_ERROR__INVALID_CONFIG]: `Invalid vesting configuration`,
    [DOPPLER_VESTING_ERROR__INVALID_LAUNCH]: `Invalid Initializer launch`,
    [DOPPLER_VESTING_ERROR__INVALID_MINT]: `Invalid base mint`,
    [DOPPLER_VESTING_ERROR__INVALID_SCHEDULE]: `Invalid vesting schedule`,
    [DOPPLER_VESTING_ERROR__INVALID_TOKEN_PROGRAM]: `Invalid base token program`,
    [DOPPLER_VESTING_ERROR__NO_TOKENS_AVAILABLE]: `No vested tokens are currently available`,
    [DOPPLER_VESTING_ERROR__TOO_MANY_ALLOCATIONS]: `Too many vesting allocations`,
    [DOPPLER_VESTING_ERROR__TOO_MANY_SCHEDULES]: `Too many vesting schedules`,
    [DOPPLER_VESTING_ERROR__UNKNOWN_SCHEDULE]: `Unknown vesting schedule`,
    [DOPPLER_VESTING_ERROR__VESTING_NOT_FUNDED]: `The launch has not funded vesting`,
  };
}

export function getDopplerVestingErrorMessage(
  code: DopplerVestingError,
): string {
  if (process.env.NODE_ENV !== 'production') {
    return (dopplerVestingErrorMessages as Record<DopplerVestingError, string>)[
      code
    ];
  }

  return 'Error message not available in production bundles.';
}

export function isDopplerVestingError<
  TProgramErrorCode extends DopplerVestingError,
>(
  error: unknown,
  transactionMessage: {
    instructions: Record<number, { programAddress: Address }>;
  },
  code?: TProgramErrorCode,
): error is SolanaError<typeof SOLANA_ERROR__INSTRUCTION_ERROR__CUSTOM> &
  Readonly<{ context: Readonly<{ code: TProgramErrorCode }> }> {
  return isProgramError<TProgramErrorCode>(
    error,
    transactionMessage,
    DOPPLER_VESTING_PROGRAM_ADDRESS,
    code,
  );
}
